/**
 * Analytics Tracking Hook
 * Provides analytics tracking functionality throughout the app
 *
 * Different from useAnalytics.ts which fetches analytics data from backend.
 * This hook provides GA4 and PWA event tracking.
 */

import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  initializeGA,
  trackPageView,
  trackEvent,
  setUserProperties,
  trackAuth,
  trackFormSubmission,
  trackAssetScan,
  trackFeatureUsage,
  trackError,
  initializeNetworkTracking,
  getAllPWAMetrics,
} from '../utils/analytics';

/**
 * Initialize analytics on app startup
 * Call this once in App.tsx or index.tsx
 */
export function useAnalyticsInitialization(): void {
  const { user } = useAuth();
  const location = useLocation();

  // Initialize GA4 and network tracking on mount
  useEffect(() => {
    initializeGA();
    initializeNetworkTracking();
  }, []);

  // Track page views on route change
  useEffect(() => {
    const pageTitle = getPageTitle(location.pathname);
    trackPageView(location.pathname, pageTitle);
  }, [location.pathname]);

  // Set user properties when user changes
  useEffect(() => {
    if (user?.id) {
      setUserProperties(user.id, {
        user_role: user.role,
        tenant_id: user.tenant_id,
      });
    }
  }, [user?.id, user?.role, user?.tenant_id]);
}

/**
 * Get page title from pathname
 */
function getPageTitle(pathname: string): string {
  const titles: Record<string, string> = {
    '/': 'Dashboard',
    '/dashboard': 'Dashboard',
    '/assets': 'Assets',
    '/forms': 'Forms',
    '/submissions': 'Submissions',
    '/users': 'Users',
    '/settings': 'Settings',
    '/analytics': 'Analytics',
    '/maintenance': 'Maintenance',
    '/scan': 'QR Scan',
    '/login': 'Login',
    '/register': 'Register',
    '/templates': 'Form Templates',
    '/asset-types': 'Asset Types',
    '/billing': 'Billing',
  };

  // Check for exact match
  if (titles[pathname]) {
    return `${titles[pathname]} | ChecksAndTicks`;
  }

  // Check for pattern matches
  if (pathname.startsWith('/assets/')) return 'Asset Details | ChecksAndTicks';
  if (pathname.startsWith('/forms/')) return 'Form Details | ChecksAndTicks';
  if (pathname.startsWith('/submissions/')) return 'Submission Details | ChecksAndTicks';
  if (pathname.startsWith('/scan/')) return 'Asset Scan | ChecksAndTicks';

  return 'ChecksAndTicks';
}

/**
 * Hook for tracking form-related events
 */
export function useFormAnalytics() {
  const trackFormView = useCallback((formId: string, formName: string) => {
    trackEvent('form_view', { form_id: formId, form_name: formName });
  }, []);

  const trackFormStart = useCallback((formId: string, formName: string) => {
    trackEvent('form_start', { form_id: formId, form_name: formName });
  }, []);

  const trackFormComplete = useCallback(
    (formId: string, formName: string, durationSeconds: number) => {
      trackFormSubmission(formId, formName, 'complete');
      trackEvent('form_complete', {
        form_id: formId,
        form_name: formName,
        duration_seconds: durationSeconds,
      });
    },
    []
  );

  const trackFormError = useCallback((formId: string, errorMessage: string) => {
    trackEvent('form_error', { form_id: formId, error_message: errorMessage });
  }, []);

  return {
    trackFormView,
    trackFormStart,
    trackFormComplete,
    trackFormError,
  };
}

/**
 * Hook for tracking asset-related events
 */
export function useAssetAnalytics() {
  const trackAssetView = useCallback((assetId: string, assetName: string) => {
    trackEvent('asset_view', { asset_id: assetId, asset_name: assetName });
  }, []);

  const trackAssetCreate = useCallback((assetId: string, assetType: string) => {
    trackEvent('asset_create', { asset_id: assetId, asset_type: assetType });
  }, []);

  const trackAssetUpdate = useCallback((assetId: string, fieldsChanged: string[]) => {
    trackEvent('asset_update', {
      asset_id: assetId,
      fields_changed: fieldsChanged.join(','),
    });
  }, []);

  const trackAssetDelete = useCallback((assetId: string) => {
    trackEvent('asset_delete', { asset_id: assetId });
  }, []);

  const trackQRScan = useCallback((assetId: string) => {
    trackAssetScan(assetId, 'qr');
  }, []);

  const trackNFCScan = useCallback((assetId: string) => {
    trackAssetScan(assetId, 'nfc');
  }, []);

  return {
    trackAssetView,
    trackAssetCreate,
    trackAssetUpdate,
    trackAssetDelete,
    trackQRScan,
    trackNFCScan,
  };
}

/**
 * Hook for tracking user-related events
 */
export function useUserAnalytics() {
  const trackLogin = useCallback((method?: string) => {
    trackAuth('login');
    if (method) {
      trackEvent('login_method', { method });
    }
  }, []);

  const trackLogout = useCallback(() => {
    trackAuth('logout');
  }, []);

  const trackRegister = useCallback((plan?: string) => {
    trackAuth('register');
    if (plan) {
      trackEvent('registration_plan', { plan });
    }
  }, []);

  const trackPasswordReset = useCallback(() => {
    trackAuth('password_reset');
  }, []);

  return {
    trackLogin,
    trackLogout,
    trackRegister,
    trackPasswordReset,
  };
}

/**
 * Hook for tracking general feature usage
 */
export function useFeatureAnalytics() {
  const track = useCallback((featureName: string, action: string, metadata?: Record<string, any>) => {
    trackFeatureUsage(featureName, action);
    if (metadata) {
      trackEvent(`feature_${featureName}_${action}`, metadata);
    }
  }, []);

  return { track };
}

/**
 * Hook for tracking errors
 */
export function useErrorAnalytics() {
  const track = useCallback((errorType: string, errorMessage: string, errorStack?: string) => {
    trackError(errorType, errorMessage, errorStack);
  }, []);

  return { track };
}

/**
 * Hook for getting PWA metrics
 */
export function usePWAMetrics() {
  const getMetrics = useCallback(() => {
    return getAllPWAMetrics();
  }, []);

  return { getMetrics };
}

export default {
  useAnalyticsInitialization,
  useFormAnalytics,
  useAssetAnalytics,
  useUserAnalytics,
  useFeatureAnalytics,
  useErrorAnalytics,
  usePWAMetrics,
};
